/**
 * Condition Registry
 *
 * Central registry for all targeting condition types.
 * Provides metadata, evaluation, and formatting for conditions.
 * NO React imports allowed in this file - selectors are in a separate registry.
 */

import { __ } from '@wordpress/i18n';
import type {
	ConditionDefinition,
	CategoryDefinition,
	IConditionRegistry,
	ConditionOperator,
	TargetingCondition,
	EvaluationContext,
	ConditionEvaluationResult,
} from './types';

// ============================================================================
// Operator Labels (shared across all conditions)
// ============================================================================

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
	is: __('is', 'gp-popups'),
	is_not: __('is not', 'gp-popups'),
	has: __('has', 'gp-popups'),
	has_not: __('does not have', 'gp-popups'),
	contains: __('contains', 'gp-popups'),
	not_contains: __('does not contain', 'gp-popups'),
	starts_with: __('starts with', 'gp-popups'),
	ends_with: __('ends with', 'gp-popups'),
	matches_regex: __('matches pattern', 'gp-popups'),
	greater_than: __('is greater than', 'gp-popups'),
	less_than: __('is less than', 'gp-popups'),
	between: __('is between', 'gp-popups'),
	exists: __('exists', 'gp-popups'),
	not_exists: __('does not exist', 'gp-popups'),
};

// ============================================================================
// Condition Registry Class
// ============================================================================

class ConditionRegistry implements IConditionRegistry {
	private conditions = new Map<string, ConditionDefinition>();
	private categories = new Map<string, CategoryDefinition>();

	// ========================================================================
	// Registration
	// ========================================================================

	/**
	 * Register a condition type
	 */
	register = (definition: ConditionDefinition): void => {
		this.conditions.set(definition.type, definition);
	};

	/**
	 * Register a custom category
	 */
	registerCategory = (category: CategoryDefinition): void => {
		this.categories.set(category.id, category);
	};

	// ========================================================================
	// Retrieval
	// ========================================================================

	/**
	 * Get a condition definition by type
	 */
	get = (type: string): ConditionDefinition | undefined => {
		return this.conditions.get(type);
	};

	/**
	 * Get all registered conditions
	 */
	getAll = (): ConditionDefinition[] => {
		return Array.from(this.conditions.values());
	};

	/**
	 * Get conditions by category
	 */
	getByCategory = (category: string): ConditionDefinition[] => {
		return this.getAll().filter((c) => c.category === category);
	};

	/**
	 * Get all categories in order
	 */
	getCategories = (): CategoryDefinition[] => {
		return Array.from(this.categories.values()).sort(
			(a, b) => (a.order ?? 100) - (b.order ?? 100)
		);
	};

	/**
	 * Get a category by ID
	 */
	getCategory = (id: string): CategoryDefinition | undefined => {
		return this.categories.get(id);
	};

	// ========================================================================
	// Condition Type Queries
	// ========================================================================

	/**
	 * Get client-side conditions (for JS evaluation)
	 */
	getClientConditions = (): string[] => {
		return this.getAll()
			.filter((c) => c.evaluationMode === 'client')
			.map((c) => c.type);
	};

	/**
	 * Get server-side conditions
	 */
	getServerConditions = (): string[] => {
		return this.getAll()
			.filter((c) => c.evaluationMode === 'server')
			.map((c) => c.type);
	};

	/**
	 * Get server conditions that require AJAX (not cacheable)
	 */
	getAjaxRequiredConditions = (): string[] => {
		return this.getAll()
			.filter(
				(c) => c.evaluationMode === 'server' && c.isCacheable === false
			)
			.map((c) => c.type);
	};

	// ========================================================================
	// Utilities
	// ========================================================================

	/**
	 * Format a condition value for display
	 */
	formatValue = (condition: TargetingCondition): string => {
		const definition = this.get(condition.type);
		if (definition?.formatValue) {
			return definition.formatValue(condition.value, condition.operator);
		}
		// Default formatting
		if (typeof condition.value === 'string') {
			return condition.value || __('(empty)', 'gp-popups');
		}
		return __('(configured)', 'gp-popups');
	};

	/**
	 * Format a complete condition summary for display
	 * Returns something like "Device is Desktop, Mobile" or "Cookie "my_cookie" exists"
	 */
	formatSummary = (condition: TargetingCondition): string => {
		const operatorLabel = this.getOperatorLabel(condition.operator);
		const valueText = this.formatValue(condition);

		// For exists/not_exists operators, the format is different
		// e.g., "my_cookie" exists (value first, then operator)
		if (
			condition.operator === 'exists' ||
			condition.operator === 'not_exists'
		) {
			return `${valueText} ${operatorLabel}`;
		}

		// Standard format: operator + value
		// e.g., "is Desktop, Mobile" or "contains /products/"
		return `${operatorLabel} ${valueText}`;
	};

	/**
	 * Get an option label for a condition type
	 */
	getOptionLabel = (type: string, optionKey: string): string => {
		const definition = this.get(type);
		return definition?.options?.[optionKey] ?? optionKey;
	};

	/**
	 * Get the human-readable label for an operator
	 */
	getOperatorLabel = (operator: ConditionOperator): string => {
		return OPERATOR_LABELS[operator] || operator;
	};

	/**
	 * Get supported operators for a condition type
	 */
	getSupportedOperators = (type: string): ConditionOperator[] => {
		return this.get(type)?.supportedOperators || ['is'];
	};

	// ========================================================================
	// Evaluation
	// ========================================================================

	/**
	 * Evaluate a condition (client-side only)
	 */
	evaluateCondition = async (
		condition: TargetingCondition,
		context: EvaluationContext
	): Promise<ConditionEvaluationResult> => {
		// Use pre-computed server result if available.
		// PHP attaches serverResult to conditions it can evaluate (cacheable server-side
		// conditions like specific_posts, content_type, etc.). This allows the frontend
		// to use the actual evaluation result instead of marking it as "skipped".
		if (condition.serverResult !== undefined) {
			return {
				conditionId: condition.id,
				conditionType: condition.type,
				matched: condition.serverResult.matched,
				reason:
					condition.serverResult.reason || 'Evaluated server-side',
				skipped: false,
			};
		}

		// Skip disabled conditions
		if (condition.enabled === false) {
			return {
				conditionId: condition.id,
				conditionType: condition.type,
				matched: true, // Disabled conditions don't block
				reason: 'Condition is disabled',
				skipped: true,
			};
		}

		const definition = this.get(condition.type);

		if (!definition) {
			return {
				conditionId: condition.id,
				conditionType: condition.type,
				matched: false,
				reason: `Unknown condition type: ${condition.type}`,
			};
		}

		// Server-side conditions without serverResult are skipped in JS
		if (definition.evaluationMode === 'server') {
			return {
				conditionId: condition.id,
				conditionType: condition.type,
				matched: true, // Assume server already validated
				skipped: true,
				reason: `Server-side condition (${condition.type}) - skipped in JS`,
			};
		}

		// Use the registered evaluator
		if (definition.evaluate) {
			return definition.evaluate(condition, context);
		}

		return {
			conditionId: condition.id,
			conditionType: condition.type,
			matched: false,
			reason: `No evaluator registered for ${condition.type}`,
		};
	};
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Global condition registry instance.
 * Use this to register and retrieve condition definitions.
 */
export const conditionRegistry = new ConditionRegistry();

// Also export the class for testing
export { ConditionRegistry };
